import { createId } from './ids';
import type { MarkupElement, NormalizedPoint } from '../types/project';

const DEFAULT_COPY_OFFSET = 0.025;

export function duplicateMarkupElement(source: MarkupElement, zIndex: number, offset = DEFAULT_COPY_OFFSET): MarkupElement {
  const duplicate = structuredClone(source);
  const maxX = Math.max(0, 1 - duplicate.bounds.width);
  const maxY = Math.max(0, 1 - duplicate.bounds.height);
  const nextX = offsetCoordinate(duplicate.bounds.x, offset, maxX);
  const nextY = offsetCoordinate(duplicate.bounds.y, offset, maxY);
  const dx = nextX - duplicate.bounds.x;
  const dy = nextY - duplicate.bounds.y;

  duplicate.id = createId('mark');
  duplicate.name = `${source.name} copy`;
  duplicate.zIndex = zIndex;
  duplicate.bounds = { ...duplicate.bounds, x: nextX, y: nextY };

  if (duplicate.type === 'line' || duplicate.type === 'arrow') {
    duplicate.points = duplicate.points.map((point) => translatePoint(point, dx, dy)) as typeof duplicate.points;
    if (duplicate.controlPoint) duplicate.controlPoint = translatePoint(duplicate.controlPoint, dx, dy);
  } else if (duplicate.type === 'pencil' || duplicate.type === 'marker' || duplicate.type === 'brush' || duplicate.type === 'pen' || duplicate.type === 'freeform' || duplicate.type === 'polyline') {
    duplicate.points = duplicate.points.map((point) => translatePoint(point, dx, dy));
  }

  return duplicate;
}

export function normalizeAutomaticNumbers(elements: MarkupElement[]): MarkupElement[] {
  let nextNumber = 1;
  return elements.map((element) => {
    if (element.type !== 'number' || element.sequenceMode !== 'auto') return element;
    const number = nextNumber++;
    return { ...element, number, name: `Number ${number}` };
  });
}

function translatePoint(point: NormalizedPoint, dx: number, dy: number): NormalizedPoint {
  return { x: point.x + dx, y: point.y + dy };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function offsetCoordinate(value: number, offset: number, maximum: number): number {
  return value + offset <= maximum
    ? clamp(value + offset, 0, maximum)
    : clamp(value - offset, 0, maximum);
}
